import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	ElementRef,
	inject,
	Input,
	input,
	OnInit,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MarkdownModule } from 'ngx-markdown';
import { Observable } from 'rxjs';

import { CarouselModalComponent } from '@valk-nx/components/ui-carousel-modal/src/lib/carousel-modal';
import { ImageComponent } from '@valk-nx/components/ui-image/src/lib/image.component';
import { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { PricingComponent } from '@valk-nx/components/ui-pricing/src/lib/pricing';
import { RibbonComponent } from '@valk-nx/components/ui-ribbon/src/lib/ribbon';
import { RoomFacilitiesComponent } from '@valk-nx/components/ui-room-facilities/src/lib/room-facilities.component';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { ImageGridComponent } from '@valk-nx/compositions/ui-image-grid/src/lib/image-grid.component';
import { cn } from '@valk-nx/core/lib/core';
import { HeadingDirective } from '@valk-nx/core/lib/directives/heading/heading.directive';
import { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import { PricingTooltip } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { RoomCardFacilitiesInterface } from '@valk-nx/rooms-store/rooms.interface';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { ngSkipHydration: 'true' },
	imports: [
		AngularSvgIconModule,
		CarouselModalComponent,
		CommonModule,
		HeadingDirective,
		ImageComponent,
		ImageGridComponent,
		LetDirective,
		MarkdownModule,
		MiniCarouselComponent,
		RibbonComponent,
		RoomFacilitiesComponent,
		RouterModule,
		PricingComponent,
		TagComponent,
		TranslatePipe,
	],
	selector: `vp-persuade-card`,
	templateUrl: './persuade-card.component.html',
})
export class PersuadeCardComponent implements OnInit, AfterViewInit {
	private readonly platformId = inject(PLATFORM_ID);
	readonly cd = inject(ChangeDetectorRef);
	private readonly viewport = inject(ViewPortService);

	@Input() images: PhotoInterface[] | undefined;
	@Input() imageVariant: 'mini-carousel' | 'single' | 'grid' = 'single';
	@Input() imageRibbon: string | undefined;
	@Input() overline: string | undefined;
	@Input({ required: true }) title: string;
	@Input() facilities: RoomCardFacilitiesInterface | undefined;
	@Input() roomSurface: number | undefined;
	@Input() content: string | undefined;
	@Input() tags: string[] | undefined;
	@Input() ctaUrl: string | undefined;
	@Input() ctaText = 'global.book-directly';
	@Input() ctaType: 'btn-primary' | 'btn-secondary' = 'btn-primary';
	@Input() ctaVariant: '' | 'btn-outline' | 'btn-text' = '';
	@Input() cardUrl: string | undefined;
	@Input() reversedCtaColumn = false;
	@Input() useVerticalCard = false;
	@Input() showReadMore = false;
	@Input() headingType: HeadingType = 'h3';
	@Input() isTransparentImage = false;

	price = input<PriceInterface | undefined>(undefined);

	pricingToolTip = input<PricingTooltip | undefined>(undefined);

	pricingConditionByType = computed(() => {
		const pricingType = this.price()?.type;

		if (pricingType === 'perPerson') {
			return 'global.price-per-person';
		}

		return 'global.price';
	});

	isMobile$: Observable<boolean>;
	isTablet$: Observable<boolean>;
	height = 0;

	carouselItems: ImageInterface[] | undefined = [];
	@ViewChild('persuadeRef') persuadeRef: ElementRef;

	cn = cn;

	constructor() {
		this.isMobile$ = this.viewport.isMobile$;
		this.isTablet$ = this.viewport.isSmallTablet$;
	}

	ngOnInit() {
		this.carouselItems = this.images?.map((image) => {
			return {
				src: image.src,
				altText: image.alt,
				width: 0,
				height: 0,
			};
		});
	}

	ngAfterViewInit() {
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.height = this.persuadeRef.nativeElement.offsetHeight;
				this.cd.detectChanges();
			});
		}
	}
}
